import { useEffect } from "react";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { recordLessonStartupPerf } from "@/features/tutor/utils/lessonStartupPerf";

const scheduleIdle = (callback: () => void): (() => void) => {
  if (typeof window === "undefined") {
    return () => {};
  }

  const requestIdle = window.requestIdleCallback;
  if (typeof requestIdle === "function") {
    const id = requestIdle(callback, { timeout: 1500 });
    return () => window.cancelIdleCallback?.(id);
  }

  const timeoutId = window.setTimeout(callback, 250);
  return () => window.clearTimeout(timeoutId);
};

type UseNextSlideHtmlPrefetchOptions = {
  startupGate?:
    | "first_audio"
    | "boot_complete_without_first_audio"
    | "not_initializing";
};

export function useNextSlideHtmlPrefetch(
  enabled = true,
  options: UseNextSlideHtmlPrefetchOptions = {},
): void {
  const lessonId = useSlideStore((state) => state.currentLessonId);
  const slides = useSlideStore((state) => state.slides);
  const currentSlideIndex = useSlideStore((state) => state.currentSlideIndex);
  const prefetchSlideHtml = useSlideStore((state) => state.prefetchSlideHtml);
  const { startupGate } = options;

  useEffect(() => {
    if (
      !enabled ||
      !Array.isArray(slides) ||
      slides.length === 0 ||
      currentSlideIndex === null ||
      currentSlideIndex === undefined
    ) {
      return;
    }

    const currentPosition = slides.findIndex(
      (slide) => slide?.slide_index === currentSlideIndex,
    );
    const nextSlide =
      currentPosition >= 0
        ? slides
            .slice(currentPosition + 1)
            .find((slide) => Boolean(slide?.slide_html))
        : null;

    if (!nextSlide?.slide_html) {
      return;
    }

    let cancelled = false;
    const perfContext = {
      lesson_id: lessonId,
      slide_index: nextSlide.slide_index,
      variant: "optimized",
      ...(startupGate ? { startup_gate: startupGate } : {}),
    };
    recordLessonStartupPerf("slide_html_prefetch_scheduled", perfContext);
    const cancelIdle = scheduleIdle(() => {
      if (cancelled) {
        return;
      }

      void prefetchSlideHtml({
        lessonId,
        slideIndex: nextSlide.slide_index,
        sourceUrl: nextSlide.slide_html,
        variant: "optimized",
      }).catch(() => undefined);
    });

    return () => {
      cancelled = true;
      cancelIdle();
    };
  }, [
    enabled,
    lessonId,
    slides,
    currentSlideIndex,
    prefetchSlideHtml,
    startupGate,
  ]);
}
